"use client";

import { useTranslations } from "next-intl";
import type * as React from "react";
import { useEffect, useState } from "react";

interface Remaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface DaoYearCountdownProps {
  /** Unix seconds at which the current DAO year ends. */
  endSec: bigint;
}

function computeRemaining(endSec: bigint): Remaining {
  const nowSec = Math.floor(Date.now() / 1000);
  const total = Math.max(0, Number(endSec) - nowSec);
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  };
}

const TimeCell: React.FC<{ value: number; unit: string }> = ({
  value,
  unit,
}) => (
  <div className="flex flex-col items-center">
    <span className="text-2xl font-bold text-text-primary font-inter" dir="ltr">
      {value}
    </span>
    <span className="text-xs text-text-secondary">{unit}</span>
  </div>
);

/** Live remaining time in the current DAO year, ticking down each second. */
export const DaoYearCountdown: React.FC<DaoYearCountdownProps> = ({
  endSec,
}) => {
  const t = useTranslations("stake");
  const [r, setR] = useState<Remaining>(() => computeRemaining(endSec));

  useEffect(() => {
    setR(computeRemaining(endSec));
    const id = setInterval(() => setR(computeRemaining(endSec)), 1000);
    return () => clearInterval(id);
  }, [endSec]);

  return (
    <div className="flex items-center justify-center gap-4">
      {/* RTL source order: باقیمانده first (rightmost), then days → … → seconds */}
      <span className="text-xs text-text-secondary">
        {t("globalStats.remaining")}
      </span>
      <TimeCell value={r.days} unit={t("globalStats.unitDay")} />
      <TimeCell value={r.hours} unit={t("globalStats.unitHour")} />
      <TimeCell value={r.minutes} unit={t("globalStats.unitMinute")} />
      <TimeCell value={r.seconds} unit={t("globalStats.unitSecond")} />
    </div>
  );
};
